/**
 * useImageFallback - Image loading with fallback strategy hook
 */

import { useState, useCallback, useEffect } from 'react';
import { logError, logInfo } from '../utils/log';
import loading from '../assets/images/loading-card.svg';

export const useImageFallback = (primaryImage, fallbackImage) => {
  const [imageSrc, setImageSrc] = useState(primaryImage);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Reset state when primary image changes
  useEffect(() => {
    // Only reset if we have a valid new primary image and we're not already showing it
    if (primaryImage && primaryImage !== imageSrc && !hasError) {
      setImageSrc(primaryImage);
      setHasError(false);
      setIsLoading(true);
    }
  }, [primaryImage]); // Removed imageSrc and hasError from dependencies to prevent loops

  const handleImageError = useCallback(() => {
    if (hasError) return; // Prevent infinite loop

    logError(`Failed to load image: ${imageSrc}`);
    setHasError(true);
    setIsLoading(false);

    // Try fallback image first
    if (fallbackImage && imageSrc !== `data:image/png;base64,${fallbackImage}`) {
      logInfo('Attempting to load fallback image');
      setImageSrc(`data:image/png;base64,${fallbackImage}`);
    } else {
      // Use default loading image as final fallback
      logInfo('Using default loading image as final fallback');
      setImageSrc(loading);
    }
  }, [fallbackImage, imageSrc, hasError]);

  const handleImageLoad = useCallback(() => {
    setIsLoading(false);
    if (hasError) {
      logInfo('Fallback image loaded successfully');
    }
  }, [hasError]);

  const resetImage = useCallback(() => {
    setImageSrc(primaryImage);
    setHasError(false);
    setIsLoading(true);
  }, [primaryImage]);

  return {
    imageSrc,
    hasError,
    isLoading,
    handleImageError,
    handleImageLoad,
    resetImage,
  };
};
